import { type ReactNode, useState } from 'react';
import { type GameState, type PlayerId } from '@qd/protocol';
import { playerColors, fontStacks } from '@qd/design-tokens';
import { useIsMobile } from '../../hooks/useIsMobile';

interface PlayerRosterProps {
  readonly gameState: GameState | null;
  readonly selfId: PlayerId | null;
}

export function PlayerRoster({ gameState, selfId }: PlayerRosterProps): ReactNode {
  // On mobile, the absolutely-positioned roster (top: 104, right: 16,
  // minWidth: 190) overlapped the map at ≤375px viewports and offered
  // no value — MobileStatusBar already shows the current player. Hide
  // outright on mobile; Play.tsx's mobile branch (MobilePlayLayout)
  // doesn't render this component anyway, but a defensive bail keeps
  // the component honest if a future caller mounts it on a mobile path.
  const isMobile = useIsMobile();
  if (isMobile) return null;
  if (!gameState) return null;

  const currentPlayerId = gameState.playerOrder[gameState.currentPlayerIdx];

  // Calculate territory count per player to show on the roster
  const territoryCounts = new Map<PlayerId, number>();
  for (const t of Object.values(gameState.territories)) {
    if (t.controllingPlayerId) {
      territoryCounts.set(
        t.controllingPlayerId,
        (territoryCounts.get(t.controllingPlayerId) || 0) + 1
      );
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        // 56px StatusBar + 40px PhaseCoach + 8px gap = below all top bars
        top: 104,
        right: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
        zIndex: 6, // below StatusBar (z:8) and PhaseCoach (z:7)
      }}
    >
      {gameState.players.map((p) => {
        const isCurrent = p.id === currentPlayerId;
        const isSelf = p.id === selfId;
        const color = playerColors[p.color].hex;
        const count = territoryCounts.get(p.id) || 0;

        return (
          <PlayerCard
            key={p.id}
            name={p.name}
            isSelf={isSelf}
            isCurrent={isCurrent}
            color={color}
            territoryCount={count}
            reserveCubes={p.reserveCubes}
          />
        );
      })}
      <style>{`
        @keyframes qd-roster-pulse {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
          70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
        @keyframes qd-roster-current-glow {
          0%, 100% { box-shadow: 0 0 0 1px var(--qd-roster-color), 0 4px 20px rgba(0,0,0,0.5); }
          50% { box-shadow: 0 0 0 2px var(--qd-roster-color), 0 4px 24px rgba(0,0,0,0.6); }
        }
      `}</style>
    </div>
  );
}

interface PlayerCardProps {
  readonly name: string;
  readonly isSelf: boolean;
  readonly isCurrent: boolean;
  readonly color: string;
  readonly territoryCount: number;
  readonly reserveCubes: number;
}

function PlayerCard({ name, isSelf, isCurrent, color, territoryCount, reserveCubes }: PlayerCardProps): ReactNode {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      style={{
        '--qd-roster-color': color,
        position: 'relative',
        background: isCurrent
          ? 'linear-gradient(135deg, rgba(30,15,15,0.96) 0%, rgba(60,10,10,0.96) 100%)'
          : 'rgba(10,20,50,0.82)',
        border: `2px solid ${isCurrent ? color : 'rgba(255,255,255,0.12)'}`,
        borderRadius: 10,
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minWidth: 190,
        maxWidth: 220,
        backdropFilter: 'blur(8px)',
        boxShadow: isCurrent
          ? `0 0 0 1px ${color}44, 0 4px 24px rgba(239,68,68,0.35)`
          : '0 2px 8px rgba(0,0,0,0.4)',
        transform: isCurrent ? 'translateX(-4px)' : 'none',
        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        animation: isCurrent ? 'qd-roster-current-glow 2s ease-in-out infinite' : 'none',
      } as React.CSSProperties}
    >
      {/* Left accent bar in player color */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: color,
          borderTopLeftRadius: 10,
          borderBottomLeftRadius: 10,
          opacity: isCurrent ? 1 : 0.5,
        }}
      />

      {/* Player color dot */}
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
          boxShadow: isCurrent ? `0 0 8px ${color}` : `inset 0 -2px 3px rgba(0,0,0,0.4)`,
          border: `1.5px solid rgba(255,255,255,0.15)`,
        }}
      />

      {/* Name and stats */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontFamily: fontStacks.display,
            fontWeight: 700,
            fontSize: 13,
            color: '#f1f5f9',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}{isSelf && <span style={{ color: color, marginLeft: 4, fontSize: 11 }}>(You)</span>}
        </span>
        <span
          style={{
            fontFamily: fontStacks.mono,
            fontSize: 10,
            color: 'rgba(148, 163, 184, 0.85)',
            letterSpacing: '0.04em',
          }}
        >
          {territoryCount} terr · <ReserveBadge count={reserveCubes} onToggle={() => setShowTooltip(v => !v)} />
        </span>
      </div>

      {/* Current turn pulse dot */}
      {isCurrent && (
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#ef4444',
            flexShrink: 0,
            animation: 'qd-roster-pulse 1.5s infinite',
          }}
        />
      )}

      {/* Reserves tooltip */}
      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            right: 'calc(100% + 8px)',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(15, 23, 42, 0.97)',
            border: '1px solid rgba(148,163,184,0.3)',
            borderRadius: 8,
            padding: '10px 12px',
            width: 200,
            color: '#cbd5e1',
            fontFamily: fontStacks.body,
            fontSize: 12,
            lineHeight: 1.5,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            pointerEvents: 'none',
            zIndex: 200,
          }}
        >
          <strong style={{ color: '#60a5fa', display: 'block', marginBottom: 4 }}>🎲 Reserve Cubes</strong>
          Reinforcements earned by conquering territories. Used to boost a territory when you can't attack.
        </div>
      )}
    </div>
  );
}

function ReserveBadge({ count, onToggle }: { count: number; onToggle: () => void }): ReactNode {
  if (count <= 0) return <span>0 res</span>;
  return (
    <span
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      title="Reserve cubes: reinforcements earned by conquering territories"
      style={{
        cursor: 'help',
        color: '#60a5fa',
        fontWeight: 700,
        pointerEvents: 'auto',
      }}
    >
      {count} res ⓘ
    </span>
  );
}
